import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Monitor, Plus, Copy, Check, Trash2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { displaysApi, type CreatedDisplay } from '../api'
import { formatDateTime, formatDate } from '@/lib/dateUtils'

/**
 * Displays — the screens in the command-and-control centre.
 *
 * An admin creates one here, carries the token to the machine that drives the wall, and
 * pastes it into /display once. Nobody ever logs in as the display: the account it creates
 * has no usable password and the sign-in endpoint refuses it.
 */

/** The screens worth putting on a wall. Read-only views that change during a recovery. */
const SUGGESTED = [
    { path: '/recover-requests', title: 'Recovery requests' },
    { path: '/recovery-management', title: 'Recovery management' },
    { path: '/readiness', title: 'Readiness' },
    { path: '/schedule', title: 'Schedule' },
]

function StatusPill({ status }: { status: string }) {
    const tone = status === 'active'
        ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
        : status === 'expired'
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`}>{status}</span>
}

export function DisplayAdminPage() {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [location, setLocation] = useState('')
    const [picked, setPicked] = useState<string[]>([SUGGESTED[0].path])
    const [created, setCreated] = useState<CreatedDisplay | null>(null)
    const [copied, setCopied] = useState(false)

    const { data: displays = [], isLoading } = useQuery({
        queryKey: ['displays'],
        queryFn: displaysApi.list,
    })

    const createMut = useMutation({
        mutationFn: displaysApi.create,
        onSuccess: (d) => {
            setCreated(d)
            setOpen(false)
            setName(''); setLocation(''); setPicked([SUGGESTED[0].path])
            qc.invalidateQueries({ queryKey: ['displays'] })
        },
    })

    const revokeMut = useMutation({
        mutationFn: displaysApi.revoke,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['displays'] }),
    })

    const copy = async (token: string) => {
        await navigator.clipboard.writeText(token)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Monitor className="h-5 w-5" />
                        {t('displays.title', 'Displays')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        {t('displays.blurb', 'Screens in the command-and-control centre. A display is read-only and never locks or signs out, so a recovery stays on the wall. It is not a person: nobody can sign in as it.')}
                    </p>
                </div>
                <Button onClick={() => setOpen(true)} className="gap-2 flex-shrink-0">
                    <Plus className="h-4 w-4" />{t('displays.add', 'Add display')}
                </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-muted/50 border-b">
                            <th className="text-left px-3 py-2 font-medium">{t('displays.colName', 'Display')}</th>
                            <th className="text-left px-3 py-2 font-medium">{t('displays.colLocation', 'Location')}</th>
                            <th className="text-left px-3 py-2 font-medium">{t('displays.colShows', 'Shows')}</th>
                            <th className="text-left px-3 py-2 font-medium">{t('displays.colLastSeen', 'Last seen')}</th>
                            <th className="text-left px-3 py-2 font-medium">{t('displays.colExpires', 'Expires')}</th>
                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.status', 'Status')}</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">{t('common.loading', 'Loading…')}</td></tr>
                        )}
                        {!isLoading && displays.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                                {t('displays.none', 'No displays yet.')}
                            </td></tr>
                        )}
                        {displays.map((d) => (
                            <tr key={d.id} className="border-b last:border-0">
                                <td className="px-3 py-2 font-medium">{d.name}</td>
                                <td className="px-3 py-2 text-muted-foreground">{d.location || '—'}</td>
                                <td className="px-3 py-2 text-muted-foreground text-xs">
                                    {(d.layout || []).map((l) => l.title || l.path).join(', ') || '—'}
                                </td>
                                <td className="px-3 py-2 text-muted-foreground text-xs">
                                    {d.lastSeenAt ? formatDateTime(d.lastSeenAt) : t('displays.neverSeen', 'never')}
                                </td>
                                <td className="px-3 py-2 text-muted-foreground text-xs">
                                    {d.expiresAt ? formatDate(d.expiresAt) : '—'}
                                </td>
                                <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                                <td className="px-3 py-2 text-right">
                                    {d.status !== 'revoked' && (
                                        <Button
                                            size="sm" variant="ghost"
                                            className="text-red-600 hover:text-red-700 gap-1.5"
                                            onClick={() => revokeMut.mutate(d.id)}
                                            disabled={revokeMut.isPending}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />{t('displays.revoke', 'Revoke')}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('displays.add', 'Add display')}</DialogTitle>
                        <DialogDescription>
                            {t('displays.addHelp', 'One per device — a machine driving four monitors is one display.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>{t('displays.colName', 'Display')}</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)}
                                placeholder={t('displays.namePlaceholder', 'C&C bridge, left screen')} />
                        </div>
                        <div>
                            <Label>{t('displays.colLocation', 'Location')}</Label>
                            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                                placeholder={t('displays.locationPlaceholder', 'Command and control centre')} />
                        </div>
                        <div>
                            <Label>{t('displays.colShows', 'Shows')}</Label>
                            <div className="space-y-1.5 mt-1.5">
                                {SUGGESTED.map((sc) => (
                                    <label key={sc.path} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={picked.includes(sc.path)}
                                            onChange={(e) => setPicked((prev) =>
                                                e.target.checked ? [...prev, sc.path] : prev.filter((p) => p !== sc.path))}
                                        />
                                        {sc.title}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button
                            disabled={!name.trim() || createMut.isPending}
                            onClick={() => createMut.mutate({
                                name: name.trim(),
                                location: location.trim() || undefined,
                                layout: SUGGESTED.filter((s) => picked.includes(s.path)),
                            })}
                        >
                            {t('displays.create', 'Create display')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* The token, shown exactly once */}
            <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{created?.name}</DialogTitle>
                        <DialogDescription>
                            {t('displays.tokenOnce', 'Copy this token now — it is never shown again. On the machine that drives the screen, open /display and paste it in once.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono bg-muted rounded p-2 break-all">{created?.token}</code>
                        <Button size="sm" variant="outline" className="gap-1.5 flex-shrink-0"
                            onClick={() => created && copy(created.token)}>
                            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            {copied ? t('displays.copied', 'Copied') : t('common.copy', 'Copy')}
                        </Button>
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3">
                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-muted-foreground">
                            {t('displays.tokenWarning', 'Anyone with this token can show these screens. It is read-only and can be revoked here at any time.')}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setCreated(null)}>{t('common.done', 'Done')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
